/**
 * Subaccounts — gasless actions: create/link, fund, defund, unlink, and API-key registration.
 *
 * Collateral is moved between master and subaccount via gasless INTERNAL transfers
 * (CLOBEngine.transferBySignature): a pure ledger move, no ERC-20 leaves the engine and no fee.
 *   - Fund (master → sub): the MASTER wallet signs (funds movement is always owner-signed, never an
 *     agent — a leaked session key can trade but can never move funds).
 *   - Defund (sub → master): the app-held SUB key signs silently (it moves its own balance, and only
 *     ever back to its master).
 *
 * Unlink is an on-chain wallet tx (there is no by-signature unlink on-chain); either party may call it.
 */
import { ethers } from "ethers";
import { Web3Provider } from "@ethersproject/providers";
import { API_URL } from "./env";
import { fetchRelayerNonce, parseRelayerJson } from "./relayerResponse";
import { createSigningKeyForWallet } from "./signing";
import { SubaccountAuthInfo } from "./subaccounts.types";
import { getSubaccountWallet, loadFor, removeSubaccountLocal, saveFor } from "./subaccounts.storage";

// ── EIP-712 type definitions (must match EIP712Types.sol exactly) ──

const ADD_SUBACCOUNT_TYPES = {
  AddSubaccount: [
    { name: "master", type: "address" },
    { name: "subaccount", type: "address" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

const LINK_SUBACCOUNT_TYPES = {
  LinkSubaccount: [
    { name: "subaccount", type: "address" },
    { name: "master", type: "address" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

const TRANSFER_BALANCE_TYPES = {
  TransferBalance: [
    { name: "from", type: "address" },
    { name: "currency", type: "address" },
    { name: "to", type: "address" },
    { name: "amount", type: "uint256" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

async function fetchNonce(relayer: string, address: string, router: string): Promise<string> {
  return fetchRelayerNonce(
    relayer,
    address,
    router,
    "Could not reach the relayer. Please try again."
  );
}

async function postRelay(relayer: string, body: unknown): Promise<any> {
  const res = await fetch(`${relayer}/relay`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const json: any = await parseRelayerJson(res, "Relay failed.");
  if (!res.ok || json?.success === false || !json?.txHash) {
    throw new Error(json?.error || "Relay failed. Please try again.");
  }
  return json;
}

/**
 * Create a subaccount: generate a keypair, link it on-chain gaslessly (master AddSubaccount signature
 * + sub LinkSubaccount signature), and persist it. Returns the new subaccount address.
 */
export async function createSubaccount(opts: {
  provider: Web3Provider;
  master: string;
  chainId: number;
  perpEngine: string;
  relayerUrl: string;
  label?: string;
}): Promise<string> {
  const relayer = opts.relayerUrl.replace(/\/+$/, "");
  const sub = ethers.Wallet.createRandom();
  const deadline = String(Math.floor(Date.now() / 1000) + 3600);
  const domain = {
    name: "PerpEngine",
    version: "1",
    chainId: opts.chainId,
    verifyingContract: opts.perpEngine,
  };

  // Both link nonces come from the shared agent-approval stream (router=agent).
  const masterNonce = await fetchNonce(relayer, opts.master, "agent");
  const subNonce = await fetchNonce(relayer, sub.address, "agent");

  // Master authorizes taking on the subaccount (wallet popup).
  const masterAuth = await opts.provider
    .getSigner()
    ._signTypedData(domain, ADD_SUBACCOUNT_TYPES, {
      master: opts.master,
      subaccount: sub.address,
      nonce: masterNonce,
      deadline,
    });
  // The subaccount consents to being traded by the master (signed silently by the generated key).
  const subConsent = await sub._signTypedData(domain, LINK_SUBACCOUNT_TYPES, {
    subaccount: sub.address,
    master: opts.master,
    nonce: subNonce,
    deadline,
  });

  await postRelay(relayer, {
    action: "linkSubaccount",
    params: {
      master: opts.master,
      subaccount: sub.address,
      masterNonce,
      subaccountNonce: subNonce,
      deadline,
      masterAuth,
      subaccountConsent: subConsent,
    },
    signature: masterAuth, // top-level field required by the relay endpoint
  });

  const list = loadFor(opts.master, opts.chainId);
  list.push({
    address: sub.address,
    privateKey: sub.privateKey,
    label: opts.label || `Subaccount ${list.length + 1}`,
    createdAt: Math.floor(Date.now() / 1000),
    linked: true,
  });
  saveFor(opts.master, opts.chainId, list);
  return sub.address;
}

/**
 * Fund a subaccount: move `amount` of `currency` from the master's engine balance to the subaccount.
 * The MASTER wallet signs (funds movement is owner-only). Relayed gaslessly.
 */
export async function fundSubaccount(opts: {
  provider: Web3Provider;
  master: string;
  chainId: number;
  spotEngine: string;
  relayerUrl: string;
  currency: string;
  subAddress: string;
  amount: string; // raw (wei) string
}): Promise<string> {
  const relayer = opts.relayerUrl.replace(/\/+$/, "");
  const deadline = String(Math.floor(Date.now() / 1000) + 3600);
  const nonce = await fetchNonce(relayer, opts.master, "spot");
  const domain = {
    name: "CLOBEngine",
    version: "1",
    chainId: opts.chainId,
    verifyingContract: opts.spotEngine,
  };
  const message = {
    from: opts.master,
    currency: opts.currency,
    to: opts.subAddress,
    amount: opts.amount,
    nonce,
    deadline,
  };
  const signature = await opts.provider
    .getSigner()
    ._signTypedData(domain, TRANSFER_BALANCE_TYPES, message);
  const json = await postRelay(relayer, {
    action: "transferBalance",
    params: { from: opts.master, to: opts.subAddress, currency: opts.currency, amount: opts.amount, nonce, deadline },
    signature,
  });
  return json.txHash;
}

/**
 * Defund a subaccount: move `amount` of `currency` from the subaccount back to the master. Signed
 * silently by the app-held SUB key (it can only send its own balance, and only to its master).
 */
export async function defundSubaccount(opts: {
  master: string;
  chainId: number;
  spotEngine: string;
  relayerUrl: string;
  currency: string;
  subAddress: string;
  amount: string; // raw (wei) string
}): Promise<string> {
  const relayer = opts.relayerUrl.replace(/\/+$/, "");
  const subWallet = getSubaccountWallet(opts.master, opts.chainId, opts.subAddress);
  if (!subWallet) throw new Error("Subaccount key not found on this device.");
  const deadline = String(Math.floor(Date.now() / 1000) + 3600);
  const nonce = await fetchNonce(relayer, opts.subAddress, "spot");
  const domain = {
    name: "CLOBEngine",
    version: "1",
    chainId: opts.chainId,
    verifyingContract: opts.spotEngine,
  };
  const message = {
    from: opts.subAddress,
    currency: opts.currency,
    to: opts.master,
    amount: opts.amount,
    nonce,
    deadline,
  };
  const signature = await subWallet._signTypedData(domain, TRANSFER_BALANCE_TYPES, message);
  const json = await postRelay(relayer, {
    action: "transferBalance",
    params: { from: opts.subAddress, to: opts.master, currency: opts.currency, amount: opts.amount, nonce, deadline },
    signature,
  });
  return json.txHash;
}

const UNLINK_ABI = ["function unlinkSubaccount(address subaccount) external"];

/**
 * Unlink a subaccount on-chain. There is no by-signature unlink, so the master pays gas for this
 * (a rare operation). Removes the local key on success.
 */
export async function unlinkSubaccount(opts: {
  provider: Web3Provider;
  master: string;
  chainId: number;
  perpEngine: string;
  subAddress: string;
}): Promise<void> {
  const engine = new ethers.Contract(opts.perpEngine, UNLINK_ABI, opts.provider.getSigner());
  const tx = await engine.unlinkSubaccount(opts.subAddress);
  await tx.wait(1);
  removeSubaccountLocal(opts.master, opts.chainId, opts.subAddress);
}

/**
 * Register the subaccount's OWN API key so the read layer (positions/orders/balances) can follow it
 * when trading as the subaccount. Signed by the app-held sub key. Returns the sub's auth info to store.
 * Requires open registration on the backend (Monad has INVITE_ONLY_ENABLED=false).
 */
export async function registerSubaccountApiKey(opts: {
  master: string;
  chainId: number;
  subAddress: string;
  referralCode?: string;
}): Promise<SubaccountAuthInfo> {
  const subWallet = getSubaccountWallet(opts.master, opts.chainId, opts.subAddress);
  if (!subWallet) throw new Error("Subaccount key not found on this device.");

  const expiry = String(Math.floor(Date.now() / 1000) + 7 * 24 * 3600);
  const sk = await createSigningKeyForWallet({ wallet: subWallet, chainId: opts.chainId, expiry });

  const res = await fetch(`${API_URL.replace(/\/+$/, "")}/register`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      account: opts.subAddress,
      signing_key: sk.registerMessage.key,
      expiry: sk.registerMessage.expiry,
      account_signature: ethers.utils.joinSignature(ethers.utils.splitSignature(sk.accountSignature)),
      signing_key_signature: sk.signingKeySignature,
      referral_code: opts.referralCode,
      no_api_key: false,
    }),
  });
  const json: any = await res.json().catch(() => ({}));
  const apiKey = json?.api_key;
  const apiSecret = json?.api_secret;
  if (!res.ok || !apiKey || !apiSecret) {
    throw new Error(json?.error || "Could not register the subaccount for trading.");
  }
  return {
    signingKey: sk.privateKey,
    account: opts.subAddress,
    apiKey,
    apiSecret,
    expiry: sk.registerMessage.expiry,
    encrypted: false,
    passwordProtected: false,
  };
}
